/**
 * Similar Product List Component
 * Independent custom element that doesn't interfere with product-recommendations
 */

class SimilarProductsLoader extends HTMLElement {
  constructor() {
    super();
    this.productId = this.dataset.productId;
    this.limit = parseInt(this.dataset.limit) || 6;
    this.blockId = this.dataset.blockId;
    this.recommendationsUrl = this.dataset.recommendationsUrl;
  }

  connectedCallback() {
    this.loadProducts();
  }

  async loadProducts() {
    try {
      // Create unique URL for this instance
      const url = `${this.recommendationsUrl}?section_id=similar-${this.blockId}&product_id=${this.productId}&limit=${this.limit}&intent=related`;
      
      const response = await fetch(url);
      const html = await response.text();
      
      // Parse the response to extract product recommendations
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Look for product recommendations data in the response
      const recommendationsElement = doc.querySelector('product-recommendations');
      if (recommendationsElement) {
        // Extract product data from the script tag in the recommendations response
        const scriptTag = recommendationsElement.querySelector('script[type="application/json"]');
        let products = [];
        
        if (scriptTag) {
          try {
            products = JSON.parse(scriptTag.textContent);
          } catch (e) {
            console.warn('Failed to parse recommendations JSON:', e);
          }
        }
        
        // If no script tag, try to extract from HTML structure
        if (products.length === 0) {
          const productElements = recommendationsElement.querySelectorAll('[data-product-id]');
          products = Array.from(productElements).map(el => ({
            id: parseInt(el.dataset.productId),
            url: el.querySelector('a')?.href || '',
            featured_image: {
              url: el.querySelector('img')?.src || '',
              alt: el.querySelector('img')?.alt || ''
            },
            title: el.querySelector('.product-title, [data-product-title]')?.textContent?.trim() || ''
          }));
        }
        
        this.renderProducts(products);
      } else {
        // Fallback: try to make a direct API call
        this.loadProductsDirectAPI();
      }
    } catch (error) {
      console.warn('Failed to load similar products:', error);
      this.showError();
    }
  }

  async loadProductsDirectAPI() {
    try {
      // Try to use the Storefront API if available
      const response = await fetch(`/recommendations/products.json?product_id=${this.productId}&limit=${this.limit}&intent=related`);
      const data = await response.json();
      
      if (data.products) {
        this.renderProducts(data.products);
      } else {
        this.showError();
      }
    } catch (error) {
      console.warn('Direct API call failed:', error);
      this.showError();
    }
  }

  renderProducts(products) {
    const container = this.querySelector('.similar-product-list');
    if (!container) return;

    // Filter out the current product and limit results
    const filteredProducts = products
      .filter(product => product.id != this.productId)
      .slice(0, this.limit);

    if (filteredProducts.length === 0) {
      this.showEmpty();
      return;
    }

    // Generate HTML for products
    const productsHTML = filteredProducts.map(product => {
      const imageUrl = this.getProductImageUrl(product);
      const productUrl = product.url || `/products/${product.handle}`;
      
      return `
        <div class="similar-product-item">
          <a href="${productUrl}" class="similar-product-link">
            ${imageUrl ? 
              `<img src="${imageUrl}" alt="${product.title || 'Product'}" style="width: 100%; height: 100%; object-fit: cover;">` :
              `<div class="similar-product-placeholder">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M21 9v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9"></path>
                  <path d="m9 22 3-5 3 5"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>`
            }
          </a>
        </div>
      `;
    }).join('');

    container.innerHTML = productsHTML;
    
    // Initialize scroll functionality
    this.initializeScrolling();
  }

  getProductImageUrl(product) {
    if (product.featured_image) {
      if (typeof product.featured_image === 'string') {
        return product.featured_image;
      }
      if (product.featured_image.url) {
        return product.featured_image.url;
      }
    }
    if (product.image) {
      return product.image;
    }
    if (product.featured_media && product.featured_media.preview_image) {
      return product.featured_media.preview_image.url;
    }
    return null;
  }

  initializeScrolling() {
    const productList = this.querySelector('.similar-product-list');
    if (!productList) return;

    // Add scroll functionality
    let isScrolling = false;
    let startX = 0;
    let scrollLeft = 0;

    // Mouse events for desktop
    productList.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      isScrolling = true;
      startX = e.pageX - productList.offsetLeft;
      scrollLeft = productList.scrollLeft;
      productList.style.cursor = 'grabbing';
      e.preventDefault();
    });

    productList.addEventListener('mouseleave', () => {
      isScrolling = false;
      productList.style.cursor = 'grab';
    });

    productList.addEventListener('mouseup', () => {
      isScrolling = false;
      productList.style.cursor = 'grab';
    });

    productList.addEventListener('mousemove', (e) => {
      if (!isScrolling) return;
      e.preventDefault();
      const x = e.pageX - productList.offsetLeft;
      const walk = (x - startX) * 2;
      productList.scrollLeft = scrollLeft - walk;
    });

    productList.style.cursor = 'grab';

    // Keyboard navigation
    productList.addEventListener('keydown', (e) => {
      const scrollAmount = 100;
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          productList.scrollLeft -= scrollAmount;
          break;
        case 'ArrowRight':
          e.preventDefault();
          productList.scrollLeft += scrollAmount;
          break;
      }
    });

    productList.setAttribute('tabindex', '0');
  }

  showError() {
    const container = this.querySelector('.similar-product-list');
    if (container) {
      container.innerHTML = '<div class="similar-product-error">Unable to load similar products</div>';
    }
  }

  showEmpty() {
    const container = this.querySelector('.similar-product-list');
    if (container) {
      container.innerHTML = '<div class="similar-product-empty">No similar products found</div>';
    }
  }
}

// Define the custom element
customElements.define('similar-products-loader', SimilarProductsLoader);

// Legacy initialization for any existing containers
class SimilarProductList {
  constructor() {
    this.containers = document.querySelectorAll('.similar-product-list-container');
    this.init();
  }

  init() {
    if (this.containers.length === 0) return;

    this.containers.forEach(container => {
      this.setupContainer(container);
    });
  }

  setupContainer(container) {
    const productList = container.querySelector('.similar-product-list');
    if (!productList) return;

    // Check if we need to randomize collection products
    if (productList.dataset.randomize === 'true') {
      this.randomizeProducts(productList);
    }

    // Only add scroll support if it's not handled by the custom element
    const customElement = container.querySelector('similar-products-loader');
    if (!customElement) {
      this.addScrollSupport(productList);
    }
  }

  randomizeProducts(productList) {
    const productsLimit = parseInt(productList.dataset.productsCount) || 6;
    const allItems = Array.from(productList.querySelectorAll('.similar-product-item[data-randomize-item]'));

    console.log('Randomizing products:', {
      productsLimit,
      totalItems: allItems.length,
      randomizeAttribute: productList.dataset.randomize
    });

    if (allItems.length === 0) {
      console.log('No items found for randomization');
      return;
    }

    // Hide all items first
    allItems.forEach(item => {
      item.style.display = 'none';
    });

    // Shuffle the array using Fisher-Yates algorithm
    const shuffled = [...allItems];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    // Show only the required number of products
    const selectedItems = shuffled.slice(0, productsLimit);

    console.log('Selected items:', selectedItems.length);

    // Show selected items
    selectedItems.forEach((item, index) => {
      item.style.display = 'block';
      console.log(`Showing item ${index + 1}:`, item);
    });
  }

  addScrollSupport(productList) {
    let isScrolling = false;
    let startX = 0;
    let scrollLeft = 0;

    productList.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      isScrolling = true;
      startX = e.pageX - productList.offsetLeft;
      scrollLeft = productList.scrollLeft;
      productList.style.cursor = 'grabbing';
      e.preventDefault();
    });

    productList.addEventListener('mouseleave', () => {
      isScrolling = false;
      productList.style.cursor = 'grab';
    });

    productList.addEventListener('mouseup', () => {
      isScrolling = false;
      productList.style.cursor = 'grab';
    });

    productList.addEventListener('mousemove', (e) => {
      if (!isScrolling) return;
      e.preventDefault();
      const x = e.pageX - productList.offsetLeft;
      const walk = (x - startX) * 2;
      productList.scrollLeft = scrollLeft - walk;
    });

    productList.style.cursor = 'grab';

    productList.addEventListener('keydown', (e) => {
      const scrollAmount = 100;
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          productList.scrollLeft -= scrollAmount;
          break;
        case 'ArrowRight':
          e.preventDefault();
          productList.scrollLeft += scrollAmount;
          break;
      }
    });

    productList.setAttribute('tabindex', '0');
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  console.log('DOM loaded, initializing SimilarProductList');
  new SimilarProductList();
});

// Re-initialize if content is dynamically loaded
document.addEventListener('shopify:section:load', () => {
  console.log('Shopify section loaded, re-initializing SimilarProductList');
  new SimilarProductList();
});

// Also try to initialize immediately if the script loads after DOM is ready
if (document.readyState === 'loading') {
  // DOM is still loading
} else {
  // DOM is already loaded
  console.log('DOM already loaded, initializing SimilarProductList immediately');
  new SimilarProductList();
}